import { api } from '../api';
import { registrarErro } from '../../utils/diagnostico';

// CORREÇÃO: Interface mantida para compatibilidade futura
interface DadosCadastro {
  usuario: {
    nome: string;
    email: string;
    usuario: string;
    senha: string;
    telefone?: string;
    fotoPerfil?: string;
    crp?: string | null;
    ativo: boolean;
    cargo: string;
  };
  empresa: {
    nome: string;
    empresa_login: string;     // Nome simplificado para login
    nome_fantasia?: string | null;
    email?: string;
    telefone?: string;
    cnpj?: string | null;
    endereco?: string;
    cep?: string | null;
    cidade?: string | null;
    estado?: string | null;
    ativo: boolean;
    // Opcional: limites iniciais caso queira customizar (normalmente omitido)
    limites_iniciais?: {
      plano_tipo?: string;
      max_pacientes?: number;
      max_usuarios?: number;
      max_psicologos?: number;
      max_atendentes?: number;
      armazenamento_gb?: number;
      modulos?: string[];
    };
  };
}

// CORREÇÃO: Interface de resposta deve ser exportada para uso no componente
export interface RespostaRegistro {
  success: boolean;
  message: string;
  usuario?: {
    id: string;
    nome: string;
  };
  empresa?: {
    id: string;
    nome: string;
  };
}

// CORREÇÃO: Exportar interface DadosCadastro para uso no componente
export type { DadosCadastro };

// Função para gerar o endpoint correto baseado no ambiente
const getRegistroEndpoint = (): string => {
  // ✅ CORREÇÃO: SEMPRE usar /api/registro
  // O Axios já está configurado com baseURL que inclui /api quando necessário
  return '/registro'; // Axios baseURL já tem /api
};

export const registroService = {
  /**
   * CORREÇÃO: Implementação correta do registro
   */
  async registrarNovoUsuario(dados: DadosCadastro): Promise<RespostaRegistro> {
    try {
      registrarErro('RegistroService', 'Iniciando registro de novo usuário', { 
        email: dados.usuario.email, 
        empresa: dados.empresa.nome 
      });
      
      // Melhorar logs para facilitar diagnóstico
      const tamanhoTotal = JSON.stringify(dados).length;
      console.log(`[RegistroService] Tamanho total dos dados: ${tamanhoTotal} bytes`);
      
      if (tamanhoTotal > 5000000) { // 5MB
        registrarErro('RegistroService', 'Dados muito grandes para envio', { tamanhoBytes: tamanhoTotal });
        throw new Error('Os dados são muito grandes para envio. Tente reduzir o tamanho da imagem.');
      }
      
      // CORREÇÃO: Validar campos obrigatórios do backend
      if (!dados.usuario.nome || !dados.usuario.email || !dados.usuario.usuario || !dados.usuario.senha) {
        throw new Error('Campos obrigatórios do usuário são necessários: nome, email, usuario, senha');
      }
      
      if (!dados.empresa.nome) {
        throw new Error('Nome da empresa é obrigatório');
      }

      // Redimensionar e comprimir imagem para evitar PayloadTooLargeError
      if (dados.usuario.fotoPerfil) {
        // Verificar tamanho inicial
        const tamanhoInicial = dados.usuario.fotoPerfil.length;
        console.log(`[RegistroService] Tamanho inicial da imagem: ${tamanhoInicial} bytes`);
        
        // Se for maior que 200KB, comprimir a imagem
        if (tamanhoInicial > 200000) {
          console.log('[RegistroService] Comprimindo imagem grande');
          dados.usuario.fotoPerfil = await comprimirImagem(dados.usuario.fotoPerfil, 0.7, 500);
          console.log(`[RegistroService] Imagem comprimida: ${dados.usuario.fotoPerfil.length} bytes`);
        }
        
        // Se após a compressão ainda for muito grande, rejeitar
        if (dados.usuario.fotoPerfil.length > 500000) {
          registrarErro('RegistroService', 'Imagem muito grande mesmo após compressão');
          throw new Error('A imagem de perfil é muito grande. Por favor, use uma imagem menor.');
        }
      }
      
      // Comprimir foto de perfil se necessário
      if (dados.usuario.fotoPerfil && dados.usuario.fotoPerfil.length > 200000) {
        registrarErro('RegistroService', 'Imagem muito grande para armazenar como base64');
        throw new Error('A imagem de perfil é muito grande. Por favor, use uma imagem menor ou de menor qualidade.');
      }
      
      // Usar a função para determinar o endpoint correto
      const endpoint = getRegistroEndpoint();
      console.log(`[CADASTRO] Endpoint final para registro: ${endpoint}`);
      console.log(`[CADASTRO] URL completa: ${api.defaults.baseURL}${endpoint}`);
      
      // Adicionar logs detalhados antes da requisição
      console.log(`[CADASTRO] Enviando requisição POST para ${endpoint}`);
      console.log('[CADASTRO] Tamanho dos dados:', JSON.stringify(dados).length, 'bytes');
      console.log('[CADASTRO] Headers:', {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Request-Source': 'webapp-cadastro'
      });
      
      const response = await api.post<RespostaRegistro>(endpoint, dados, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Request-Source': 'webapp-cadastro'  // Identificador da origem para diagnóstico
        },
        // ✅ Aumentar timeout para 60 segundos (upload de foto pode demorar)
        timeout: 60000
      });
      
      console.log('[RegistroService] Usuário registrado com sucesso', { 
        usuarioId: response.data.usuario?.id,
        status: response.status,
        statusText: response.statusText
      });
      
      return response.data;
    } catch (error: any) {
      // Melhorar o log de erros com detalhes mais específicos
      registrarErro('RegistroService', 'Erro ao registrar usuário', {
        message: error.message,
        code: error.code,
        name: error.name,
        hasResponse: !!error.response,
        status: error.response?.status,
        statusText: error.response?.statusText,
        responseData: error.response?.data,
        stack: error.stack
      });
      
      throw error;
    }
  },
  
  /**
   * Verifica se um email já está cadastrado
   * @param email Email para verificar
   * @returns Promise<boolean> true se já existe, false se disponível
   */
  async verificarEmailExistente(email: string): Promise<boolean> {
    try {
      const response = await api.get<{exists: boolean}>(`/auth/verifica-email?email=${encodeURIComponent(email)}`);
      return response.data.exists;
    } catch (error) {
      registrarErro('RegistroService', 'Erro ao verificar email', error);
      // Em caso de erro, presumimos que o email pode estar em uso
      return true;
    }
  },
  
  /**
   * Verifica se um nome de usuário já está cadastrado
   * @param usuario Nome de usuário para verificar
   * @returns Promise<boolean> true se já existe, false se disponível
   */
  async verificarUsuarioExistente(usuario: string): Promise<boolean> {
    try {
      const response = await api.get<{exists: boolean}>(`/auth/verifica-usuario?usuario=${encodeURIComponent(usuario)}`);
      return response.data.exists;
    } catch (error) {
      registrarErro('RegistroService', 'Erro ao verificar nome de usuário', error);
      // Em caso de erro, presumimos que o usuário pode estar em uso
      return true;
    }
  }
};

/**
 * Função para comprimir imagem em base64
 * @param base64Str String da imagem em base64
 * @param qualidade Qualidade da compressão (0-1)
 * @param maxLado Tamanho máximo em pixels para o maior lado
 * @returns Promise<string> Nova string base64 comprimida
 */
async function comprimirImagem(base64Str: string, qualidade: number = 0.7, maxLado: number = 500): Promise<string> {
  return new Promise((resolve, reject) => {
    try {
      // Criar elemento de imagem
      const img = new Image();
      img.onload = () => {
        // Calcular dimensões mantendo proporção
        let largura = img.width;
        let altura = img.height;
        
        if (largura > altura && largura > maxLado) {
          altura = altura * (maxLado / largura);
          largura = maxLado;
        } else if (altura > maxLado) {
          largura = largura * (maxLado / altura);
          altura = maxLado;
        }
        
        // Criar canvas para redimensionar
        const canvas = document.createElement('canvas');
        canvas.width = largura;
        canvas.height = altura;
        
        // Desenhar a imagem redimensionada
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Não foi possível criar contexto de canvas'));
          return;
        }
        
        ctx.drawImage(img, 0, 0, largura, altura);
        
        // Converter para base64 com qualidade reduzida
        const novoBase64 = canvas.toDataURL('image/jpeg', qualidade);
        resolve(novoBase64);
      };
      
      img.onerror = () => {
        reject(new Error('Erro ao carregar imagem para compressão'));
      };
      
      img.src = base64Str;
    } catch (error) {
      reject(error);
    }
  });
}
